import React, { useState, useEffect } from 'react';
import { systemService } from '../../../services';
import { RefreshCw, CheckCircle, XCircle, Cloud, Key, AlertCircle } from 'lucide-react';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useToast } from '../../../components/common/ToastContainer';
import { formatDate } from '../../../utils/formatDate';
import { useSettingsStore } from '../../../stores/settingsStore';
import { usePermissions } from '../../../hooks/usePermissions';

const GlobalVSATab = () => {
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [credentials, setCredentials] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [form, setForm] = useState({
    clientId: '',
    clientSecret: '',
    globalVsaUrl: 'http://localhost:4001'
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [credsData, statusData] = await Promise.all([
        systemService.getGlobalVsaCredentials(),
        systemService.getGlobalVsaStatus()
      ]);

      setCredentials(credsData);
      setStatus(statusData);

      if (credsData?.configured) {
        setForm(prev => ({
          ...prev,
          clientId: credsData.clientId,
          globalVsaUrl: credsData.globalVsaUrl,
          // Don't populate secret for security
        }));
      }
    } catch (e) {
      console.error('Failed to load Global VSA data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.clientId || !form.clientSecret || !form.globalVsaUrl) {
      toast.warning('Missing Fields', 'All fields are required');
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleSaveCredentials = async () => {
    try {
      setSaving(true);
      setShowConfirmDialog(false);
      await systemService.saveGlobalVsaCredentials(form);
      toast.success('Credentials Saved', 'Global VSA credentials have been saved successfully');
      await loadData();
      // Clear the secret field after successful save
      setForm(prev => ({ ...prev, clientSecret: '' }));
    } catch (e: any) {
      toast.error('Save Failed', e.response?.data?.message || e.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Credentials Configuration */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4 flex items-center">
          <Key className="w-5 h-5 mr-2 text-blue-600" />
          Orchestrator Credentials
        </h3>

        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 mr-3 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">About Orchestrator Credentials</p>
                <p>These credentials are obtained from the Global VSA Administrator. They allow this orchestrator to register multiple tenants with the Global VSA service for edge discovery.</p>
              </div>
            </div>
          </div>

          {credentials?.configured && (
            <div className="flex items-center text-green-600 font-medium mb-4">
              <CheckCircle className="w-5 h-5 mr-2" />
              Credentials Configured
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
          <div>
            <label className="block text-sm font-medium mb-1">
              Global VSA URL
            </label>
            <input
              type="url"
              required
              placeholder="http://global-vsa.example.com:4001"
              className="w-full border rounded px-3 py-2 focus:ring focus:ring-blue-200"
              value={form.globalVsaUrl}
              onChange={e => setForm({ ...form, globalVsaUrl: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">Base URL of the Global VSA API service</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Client ID
            </label>
            <input
              type="text"
              required
              placeholder="orch-xxxxxxxxxxxxx"
              className="w-full border rounded px-3 py-2 focus:ring focus:ring-blue-200 font-mono text-sm"
              value={form.clientId}
              onChange={e => setForm({ ...form, clientId: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">OAuth Client ID provided by Global VSA Admin</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Client Secret
            </label>
            <input
              type="password"
              required={!credentials?.configured}
              placeholder={credentials?.configured ? '••••••••••••••••' : 'Enter client secret'}
              className="w-full border rounded px-3 py-2 focus:ring focus:ring-blue-200 font-mono text-sm"
              value={form.clientSecret}
              onChange={e => setForm({ ...form, clientSecret: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">
              {credentials?.configured
                ? 'Leave blank to keep existing secret'
                : 'OAuth Client Secret provided by Global VSA Admin'}
            </p>
          </div>

          {canUpdate && (
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center transition-colors"
            >
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              {credentials?.configured ? 'Update Credentials' : 'Save Credentials'}
            </button>
          )}
        </form>
      </div>

      {/* Registration Status */}
      {credentials?.configured && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <Cloud className="w-5 h-5 mr-2 text-blue-600" />
            Tenant Registrations
          </h3>

          {status?.tenantsRegistered === 0 ? (
            <div className="text-gray-500 mb-4 flex items-center">
              <XCircle className="w-5 h-5 mr-2 text-gray-400" />
              No tenants registered yet
            </div>
          ) : (
            <div className="mb-4">
              <div className="flex items-center text-green-600 font-medium mb-4">
                <CheckCircle className="w-5 h-5 mr-2" />
                {status.tenantsRegistered} Tenant{status.tenantsRegistered !== 1 ? 's' : ''} Registered
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tenant ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registered At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {status.tenants?.map((tenant: any) => (
                      <tr key={tenant.tenantId}>
                        <td className="px-4 py-3 text-sm font-mono">{tenant.tenantId}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${tenant.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : tenant.status === 'pending_approval'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                            {tenant.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(tenant.registeredAt, useSettingsStore.getState().getDateFormatSettings())}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
            <p className="font-medium mb-2">To register a tenant:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Navigate to <span className="font-mono bg-white px-1 rounded">Admin → Tenants</span></li>
              <li>Click on a tenant</li>
              <li>Click the <span className="font-semibold">"Register with Global VSA"</span> button</li>
            </ol>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleSaveCredentials}
        title="Save Credentials"
        message="Save Global VSA credentials? This will allow tenants to be registered with Global VSA."
        confirmText="Save Credentials"
        cancelText="Cancel"
        variant="info"
        isLoading={saving}
      />
    </div>
  );
};

export default GlobalVSATab;
